import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const SpeechSynthesis = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Permission check
  const { hasFeatureAccess, getPlanInfo, getRequiredPlanForFeature } = usePermission();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  const [activeTab, setActiveTab] = useState('voices'); // 'voices' or 'synthesis'
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [favoriteVoices, setFavoriteVoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGender, setFilterGender] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');

  const [formData, setFormData] = useState({
    text: '',
    slow: false,
  });

  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [error, setError] = useState('');

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamProgress, setStreamProgress] = useState(0);
  const [streamChunks, setStreamChunks] = useState([]);
  const [streamingAudio, setStreamingAudio] = useState(null);
  const [isStreamPlaying, setIsStreamPlaying] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [totalSegments, setTotalSegments] = useState(0);

  // Preview state
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);

  // Voice library state
  const [voiceLibrary, setVoiceLibrary] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);

  const navigationWarning = useNavigationWarning(generating, 'Speech is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'content-generation');

  // Load voices from API and favorites from localStorage
  useEffect(() => {
    const loadVoicesAndFavorites = async () => {
      try {
        // Load favorites from localStorage
        const saved = localStorage.getItem('favoriteVoices');
        if (saved) {
          setFavoriteVoices(JSON.parse(saved));
        }

        // Fetch voices from API
        const response = await contentAPI.getVoices();
        if (response.data.success) {
          setVoiceLibrary(response.data.data.voices);
        }
      } catch (error) {
        console.error('Failed to load voices:', error);
        // Fallback to empty array if API fails
        setVoiceLibrary([]);
      } finally {
        setLoadingVoices(false);
      }
    };

    loadVoicesAndFavorites();
  }, []);

  // Handle pre-filled data from navigation (e.g., from Workspace re-create)
  useEffect(() => {
    const prefillData = location.state?.prefillData;
    if (prefillData && voiceLibrary.length > 0) {
      // Pre-fill text
      if (prefillData.text) {
        setFormData(prev => ({
          ...prev,
          text: prefillData.text
        }));
      }

      // Pre-fill audio URL
      if (prefillData.audioUrl) {
        const fullAudioUrl = `https://api.getaizest.com${prefillData.audioUrl}`;
        setAudioUrl(fullAudioUrl);
      }

      // Select voice if specified
      if (prefillData.voice) {
        const voice = voiceLibrary.find(v => v.id === prefillData.voice || v.name === prefillData.voice);
        if (voice) {
          setSelectedVoice(voice);
        }
      }

      // Switch to synthesis tab
      setActiveTab('synthesis');
    }
  }, [location.state, voiceLibrary]);

  // Filter voices based on search and filters
  const filteredVoices = voiceLibrary.filter(voice => {
    const matchesSearch = voice.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         voice.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (voice.accent && voice.accent.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesGender = filterGender === 'all' || voice.gender.toLowerCase() === filterGender;
    const matchesAge = filterLanguage === 'all' || voice.age === filterLanguage;

    return matchesSearch && matchesGender && matchesAge;
  });

  const toggleFavorite = (voiceId) => {
    const newFavorites = favoriteVoices.includes(voiceId)
      ? favoriteVoices.filter(id => id !== voiceId)
      : [...favoriteVoices, voiceId];

    setFavoriteVoices(newFavorites);
    localStorage.setItem('favoriteVoices', JSON.stringify(newFavorites));
  };

  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice);
    setActiveTab('synthesis');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Validate text length (max 5000 characters)
    if (formData.text.length > 5000) {
      setError('Text is too long. Maximum length is 5000 characters.');
      return;
    }

    // Validate voice selection
    if (!selectedVoice) {
      setError('Please select a voice first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('speech-synthesis')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'AI Speech Synthesis',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('speech-synthesis')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    setIsStreaming(true);
    setError('');
    setAudioUrl('');
    setStreamChunks([]);
    setStreamProgress(0);
    setCurrentSegment(0);
    setTotalSegments(0);

    try {
      const chunks = [];

      await contentAPI.generateSpeechStreaming(
        {
          text: formData.text,
          voice: selectedVoice.id,
          slow: formData.slow
        },
        // onChunk callback
        (chunkData) => {
          console.log('Received chunk:', chunkData.chunkIndex);
          chunks.push(chunkData.data);
          setStreamChunks([...chunks]);
          setStreamProgress(chunkData.chunkIndex);
          if (chunkData.segmentIndex) {
            setCurrentSegment(chunkData.segmentIndex);
          }
        },
        // onComplete callback
        (result) => {
          console.log('Stream complete:', result);
          console.log('Result audioUrl:', result.audioUrl);
          setIsStreaming(false);
          setGenerating(false);

          // Set final audio URL
          if (result.audioUrl) {
            const fullAudioUrl = `https://api.getaizest.com${result.audioUrl}`;
            console.log('Setting audioUrl to:', fullAudioUrl);
            setAudioUrl(fullAudioUrl);
          } else {
            console.error('No audioUrl in result:', result);
            setError('Audio generation completed but no URL received');
          }
        },
        // onError callback
        (error) => {
          console.error('Stream error:', error);
          setError(error.message || 'Failed to generate speech');
          setGenerating(false);
          setIsStreaming(false);
        },
        // onSegment callback
        (segmentData) => {
          console.log('Processing segment:', segmentData.segmentIndex);
          setCurrentSegment(segmentData.segmentIndex);
          setTotalSegments(segmentData.totalSegments);
        },
        // onInfo callback
        (infoData) => {
          console.log('Stream info:', infoData.message);
          setTotalSegments(infoData.totalTextChunks || 0);
        }
      );

    } catch (err) {
      console.error('Speech generation error:', err);
      setError(err.message || 'Failed to generate speech');
      setGenerating(false);
      setIsStreaming(false);
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `${selectedVoice.name}-speech.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreview = async (voice, event) => {
    event.stopPropagation(); // Prevent voice selection

    // If already previewing this voice, toggle pause/play
    if (previewingVoice === voice.id && previewAudio) {
      if (previewAudio.paused) {
        // Resume playing
        previewAudio.play();
        setIsPreviewPlaying(true);
      } else {
        // Pause playing
        previewAudio.pause();
        setIsPreviewPlaying(false);
      }
      return;
    }

    // Stop any currently playing preview
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      setPreviewAudio(null);
      setIsPreviewPlaying(false);
    }

    setPreviewingVoice(voice.id);
    setIsPreviewPlaying(true);

    try {
      // Use pre-cached preview audio
      const previewUrl = voice.previewUrl || `/audio/previews/${voice.id}.mp3`;
      const fullPreviewUrl = `https://api.getaizest.com${previewUrl}`;

      // Create audio object
      const audio = new Audio(fullPreviewUrl);

      // Setup event listeners
      audio.addEventListener('ended', () => {
        setPreviewingVoice(null);
        setPreviewAudio(null);
        setIsPreviewPlaying(false);
      });

      audio.addEventListener('pause', () => {
        setIsPreviewPlaying(false);
      });

      audio.addEventListener('play', () => {
        setIsPreviewPlaying(true);
      });

      audio.addEventListener('error', (e) => {
        console.error(`Failed to load preview audio for ${voice.name}:`, e);
        setPreviewingVoice(null);
        setPreviewAudio(null);
        setIsPreviewPlaying(false);
        setError(`Unable to load preview for ${voice.name}. Please try again later.`);
        // Clear error after 3 seconds
        setTimeout(() => setError(''), 3000);
      });

      // Load and play pre-cached audio
      setPreviewAudio(audio);
      await audio.play();
    } catch (err) {
      console.error('Preview error:', err);
      setPreviewingVoice(null);
      setPreviewAudio(null);
      setIsPreviewPlaying(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.currentTime = 0;
      }
    };
  }, [previewAudio]);

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <svg className="w-10 h-10 text-blue-600" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                  <path d="M762.24 480.64c-17.28 0-31.36 14.08-31.36 31.36v28.8c0 52.266667-20.906667 103.68-58.026667 140.586667a199.253333 199.253333 0 0 1-139.946666 58.026666h-58.666667c-52.266667 0-103.253333-21.12-140.16-58.026666a198.933333 198.933333 0 0 1-58.026667-140.586667V512c0-17.28-14.08-31.36-31.36-31.36-17.28 0-31.36 14.08-31.36 31.36v28.8c0.426667 142.933333 116.266667 259.84 258.773334 261.546667v52.266666h-54.826667c-17.28 0-31.36 14.08-31.36 31.36 0 17.28 14.08 31.36 31.36 31.36h172.586667c17.28 0 31.36-14.08 31.36-31.36 0-17.28-14.08-31.36-31.36-31.36h-54.826667v-52.266666c142.72-1.706667 258.56-118.4 258.773333-261.546667V512c0-17.28-14.08-31.36-31.36-31.36h-0.213333z"/>
                  <path d="M503.466667 687.146667c96.64 0 175.146667-78.72 175.146666-175.146667V281.813333C678.613333 185.386667 600.106667 106.666667 503.466667 106.666667c-96.64 0-175.36 78.72-175.36 175.146666V512c0 96.64 78.72 175.146667 175.146666 175.146667h0.213334z m-112.426667-405.333334c0-61.866667 50.346667-112.426667 112.426667-112.426666a112.426667 112.426667 0 0 1 112.426666 112.426666V512a112.426667 112.426667 0 0 1-224.853333 0V281.813333z"/>
                </svg>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    AI Speech Synthesis
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Choose from professional voices and convert text to natural-sounding speech
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('voices')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'voices'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>Explore Voices</span>
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('synthesis')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'synthesis'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Text to Speech</span>
                  </span>
                </button>
              </nav>
            </div>

            {/* Voice Gallery Tab */}
            {activeTab === 'voices' && (
              <div>
                {/* Search and Filters */}
                <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search */}
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search Voices
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search by name or description..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>

                    {/* Gender Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gender
                      </label>
                      <select
                        value={filterGender}
                        onChange={(e) => setFilterGender(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Genders</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="neutral">Neutral</option>
                      </select>
                    </div>

                    {/* Age Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Voice Age
                      </label>
                      <select
                        value={filterLanguage}
                        onChange={(e) => setFilterLanguage(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Ages</option>
                        <option value="Young">Young</option>
                        <option value="Young Adult">Young Adult</option>
                        <option value="Adult">Adult</option>
                        <option value="Mature">Mature</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Loading State */}
                {loadingVoices && (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                    <p className="mt-4 text-gray-600">Loading voices...</p>
                  </div>
                )}

                {/* Voice Grid */}
                {!loadingVoices && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredVoices.map((voice) => (
                    <div
                      key={voice.id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col"
                    >
                      {/* Avatar Header */}
                      <div className={`${voice.color} p-8 relative min-h-[160px] flex items-center justify-center`}>
                        <button
                          onClick={() => toggleFavorite(voice.id)}
                          className="absolute top-3 right-3 text-white hover:scale-110 transition-transform z-10"
                        >
                          {favoriteVoices.includes(voice.id) ? (
                            <svg className="w-6 h-6 fill-current drop-shadow-md" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          )}
                        </button>
                        {voice.avatarUrl ? (
                          <img
                            src={`https://api.getaizest.com${voice.avatarUrl}`}
                            alt={voice.name}
                            className="w-24 h-24 rounded-full object-cover shadow-2xl ring-4 ring-white ring-opacity-30"
                          />
                        ) : (
                          <div className="text-6xl text-center text-white drop-shadow-lg">
                            {voice.avatar}
                          </div>
                        )}
                      </div>

                      {/* Voice Info */}
                      <div className="p-4 flex flex-col flex-1">
                        {/* Content area that grows */}
                        <div className="flex-grow">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {voice.name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-3">
                            {voice.description}
                          </p>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="font-medium w-20">Accent:</span>
                              <span>{voice.accent}</span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="font-medium w-20">Gender:</span>
                              <span>{voice.gender}</span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="font-medium w-20">Style:</span>
                              <span>{voice.style}</span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="font-medium w-20">Best for:</span>
                              <span className="truncate">{voice.useCase}</span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons - always at bottom */}
                        <div className="space-y-2 mt-auto">
                          {/* Preview Button */}
                          <button
                            onClick={(e) => handlePreview(voice, e)}
                            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                              previewingVoice === voice.id && isPreviewPlaying
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : previewingVoice === voice.id
                                ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {previewingVoice === voice.id ? (
                              isPreviewPlaying ? (
                                <>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  <span>Pause</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                  </svg>
                                  <span>Resume</span>
                                </>
                              )
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                                <span>Preview Voice</span>
                              </>
                            )}
                          </button>

                          {/* Select Button */}
                          <button
                            onClick={() => handleVoiceSelect(voice)}
                            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                          >
                            <span>Select Voice</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}

                {!loadingVoices && filteredVoices.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No voices found</h3>
                    <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters</p>
                  </div>
                )}
              </div>
            )}

            {/* Text to Speech Tab */}
            {activeTab === 'synthesis' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Input Area */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Text Input Form */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Enter Your Text
                    </h2>

                    <form onSubmit={handleGenerate} className="space-y-4">
                      {/* Text Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Text to Convert to Speech
                        </label>
                        <textarea
                          name="text"
                          value={formData.text}
                          onChange={handleInputChange}
                          rows="10"
                          maxLength="5000"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent resize-none ${
                            formData.text.length > 5000
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-blue-500'
                          }`}
                          placeholder="Type or paste your text here..."
                          required
                          onInvalid={(e) => e.target.setCustomValidity('Please enter the text you want to convert to speech')}
                          onInput={(e) => e.target.setCustomValidity('')}
                        ></textarea>
                        <div className="mt-2 flex items-center justify-between text-sm">
                          <span className={formData.text.length > 5000 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                            {formData.text.length} / 5000 characters
                          </span>
                          {formData.text.length > 5000 && (
                            <span className="text-red-600 font-medium">Text exceeds maximum length</span>
                          )}
                        </div>
                      </div>

                      {/* Options */}
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            name="slow"
                            checked={formData.slow}
                            onChange={(e) => setFormData(prev => ({ ...prev, slow: e.target.checked }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Slower speech (easier to understand)
                          </label>
                        </div>
                      </div>

                      {/* Generate Button */}
                      <button
                        type="submit"
                        disabled={generating || !formData.text || formData.text.length > 5000}
                        className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                          generating || !formData.text || formData.text.length > 5000
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {generating ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating Speech...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                            </svg>
                            Generate Speech
                          </span>
                        )}
                      </button>

                      {/* Streaming Progress */}
                      {isStreaming && streamProgress > 0 && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-blue-900">
                              Generating speech stream...
                              {totalSegments > 1 && (
                                <span className="ml-2 text-blue-700">
                                  (Segment {currentSegment}/{totalSegments})
                                </span>
                              )}
                            </span>
                            <span className="text-sm text-blue-700">
                              {streamProgress} chunks received
                            </span>
                          </div>
                          <div className="w-full bg-blue-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min((streamProgress / 10) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </form>
                  </div>

                  {/* Audio Output */}
                  {(audioUrl || error) && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Generated Audio
                      </h2>

                      {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex">
                            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <div className="ml-3">
                              <p className="text-sm text-red-800">{error}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {audioUrl && (
                        <div className="space-y-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex">
                              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <div className="ml-3">
                                <p className="text-sm text-green-800 font-medium">Speech generated successfully!</p>
                              </div>
                            </div>
                          </div>

                          {/* Audio Player */}
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <audio
                              controls
                              src={audioUrl}
                              className="w-full"
                              preload="metadata"
                              crossOrigin="anonymous"
                            >
                              Your browser does not support the audio element.
                            </audio>
                          </div>

                          {/* Download Button */}
                          <button
                            onClick={handleDownload}
                            className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download MP3
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sidebar - Selected Voice Info */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Selected Voice
                    </h2>

                    {selectedVoice ? (
                      <div>
                        {/* Voice Avatar */}
                        <div className={`${selectedVoice.color} rounded-lg p-8 mb-4 flex items-center justify-center min-h-[160px]`}>
                          {selectedVoice.avatarUrl ? (
                            <img
                              src={`https://api.getaizest.com${selectedVoice.avatarUrl}`}
                              alt={selectedVoice.name}
                              className="w-28 h-28 rounded-full object-cover shadow-lg"
                              style={{ mixBlendMode: 'normal' }}
                            />
                          ) : (
                            <div className="text-7xl text-center text-white">
                              {selectedVoice.avatar}
                            </div>
                          )}
                        </div>

                        {/* Voice Details */}
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {selectedVoice.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          {selectedVoice.description}
                        </p>

                        <div className="space-y-3 mb-4">
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 w-24">Accent:</span>
                            <span className="text-sm text-gray-600">{selectedVoice.accent}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 w-24">Gender:</span>
                            <span className="text-sm text-gray-600">{selectedVoice.gender}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 w-24">Age:</span>
                            <span className="text-sm text-gray-600">{selectedVoice.age}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 w-24">Style:</span>
                            <span className="text-sm text-gray-600">{selectedVoice.style}</span>
                          </div>
                          <div className="flex items-start">
                            <span className="text-sm font-medium text-gray-700 w-24">Best for:</span>
                            <span className="text-sm text-gray-600">{selectedVoice.useCase}</span>
                          </div>
                        </div>

                        <button
                          onClick={() => setActiveTab('voices')}
                          className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                        >
                          Change Voice
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <p className="text-sm text-gray-500 mb-4">No voice selected</p>
                        <button
                          onClick={() => setActiveTab('voices')}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Browse Voices →
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="h-5 w-5 text-blue-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-900">Tips</h3>
                        <div className="mt-2 text-sm text-blue-800">
                          <ul className="list-disc list-inside space-y-1">
                            <li>Use punctuation for natural pauses</li>
                            <li>Add emphasis with capital letters</li>
                            <li>Break long text into paragraphs</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default SpeechSynthesis;
